import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Users } from 'lucide-react'
import { api } from '../../lib/api'
import { Table, type Colonna } from '../../components/ui/Table'
import { Badge } from '../../components/ui/Badge'
import { EmptyState } from '../../components/ui/EmptyState'
import { etichettaPiano, variantePiano, formatData } from '../../lib/format'
import pagina from '../pagine.module.css'

interface TeamRiga {
  team: { id: string; nome: string; piano: string; attivo: boolean; maxMembri: number; createdAt: string }
  owner: { nome: string; cognome: string; email: string } | null
  membri: number
  sessioni_mese: number
}

export function AdminTeams() {
  const navigate = useNavigate()
  const q = useQuery({
    queryKey: ['admin', 'teams'],
    queryFn: async () => (await api.get<{ dati: TeamRiga[] }>('/superadmin/teams')).data.dati,
  })

  if (q.isLoading) return <div className={pagina.skeleton}>Caricamento…</div>
  if (q.error || !q.data) return <EmptyState titolo="Errore caricamento" />

  const righe = q.data.map((r) => ({ ...r, id: r.team.id }))

  const colonne: Colonna<typeof righe[0]>[] = [
    {
      chiave: 'nome', intestazione: 'Team',
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.team.nome}</div>
          {r.owner && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              Owner: {r.owner.nome} {r.owner.cognome}
            </div>
          )}
        </div>
      ),
    },
    {
      chiave: 'piano', intestazione: 'Piano',
      render: (r) => <Badge variante={variantePiano(r.team.piano)}>{etichettaPiano(r.team.piano)}</Badge>,
      larghezza: '120px',
    },
    {
      chiave: 'membri', intestazione: 'Membri',
      allineamento: 'right',
      render: (r) => <span className="mono">{r.membri}/{r.team.maxMembri}</span>,
      larghezza: '110px',
    },
    {
      chiave: 'mese', intestazione: 'Sess. mese',
      allineamento: 'right',
      render: (r) => <span className="mono">{r.sessioni_mese}</span>,
      larghezza: '110px',
    },
    {
      chiave: 'attivo', intestazione: 'Stato',
      render: (r) => r.team.attivo
        ? <Badge variante="success">Attivo</Badge>
        : <Badge variante="warning">Sospeso</Badge>,
      larghezza: '110px',
    },
    {
      chiave: 'data', intestazione: 'Creato',
      render: (r) => <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{formatData(r.team.createdAt)}</span>,
      larghezza: '120px',
    },
  ]

  return (
    <div className={pagina.wrap}>
      <div className={pagina.header}>
        <div>
          <h1 className={pagina.titolo}>Teams</h1>
          <div className={pagina.sottotitolo}>{righe.length} team registrati</div>
        </div>
      </div>

      <Table
        colonne={colonne}
        righe={righe}
        onClickRiga={(r) => navigate(`/admin/teams/${r.team.id}`)}
        vuoto={<EmptyState icona={<Users size={40} />} titolo="Nessun team" />}
      />
    </div>
  )
}
